import { Calendar, Edit } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { getCustomerDisplayName } from "@/domains/customer/customers/utils/customer-utils";
import { Can } from "@/lib/authorization";
import { BackButton, BaseNavigationBar } from "@/lib/components/navigation";
import { PageContent } from "@/lib/components/page-content";
import { useDialogRouterSimple } from "@/lib/dialog/use-dialog-router-simple";
import { useIsMobile } from "@/lib/hooks/use-mobile";
import { CustomerDetailView } from "../components/customer-detail-view";
import { useCustomerDetail } from "../hooks/use-customer-detail";
import { useCustomerDetailUIStore } from "../store/use-customer-detail-ui-store";

export function CustomerDetailPage() {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { t } = useTranslation("customers");
  const { openEditDialog } = useCustomerDetailUIStore();
  const { openDialog } = useDialogRouterSimple();

  const { customer, loading } = useCustomerDetail({ customerId: customerId ?? "" });

  const handleBack = () => {
    navigate("/customers");
  };

  const handleEdit = () => {
    openEditDialog();
  };

  const handleCall = () => {
    if (customer?.primaryPhone?.phone) {
      window.location.href = `tel:${customer.primaryPhone.phone}`;
    }
  };

  const handleEmail = () => {
    if (customer?.email) {
      window.location.href = `mailto:${customer.email}`;
    }
  };

  const handleBookAppointment = () => {
    if (customer?.id) {
      openDialog({
        type: "appointment-create",
        params: {
          customerId: customer.id,
        },
      });
    }
  };

  // Show loading state while fetching customer
  if (loading && !customer) {
    return (
      <>
        <BaseNavigationBar
          title={t("customerDetails")}
          subtitle={t("detail.loading")}
          backButton={<BackButton onBack={handleBack} />}
        />
        <PageContent>
          <div className="flex items-center justify-center h-64">
            <Spinner size="lg" />
          </div>
        </PageContent>
      </>
    );
  }

  // Show customer not found after loading is complete
  if (!loading && !customer) {
    return (
      <>
        <BaseNavigationBar
          title={t("customerDetails")}
          subtitle={t("detail.notFound.title")}
          backButton={<BackButton onBack={handleBack} />}
        />
        <PageContent>
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <h2 className="text-lg font-semibold mb-2">{t("detail.notFound.title")}</h2>
              <p className="text-muted-foreground">{t("detail.notFound.description")}</p>
            </div>
          </div>
        </PageContent>
      </>
    );
  }

  if (!customer) return null;

  // Show only Book and Edit buttons in nav bar
  const trailingElements = [
    // Show Book button only on desktop
    ...(!isMobile
      ? [
          <Button key="book" size="sm" onClick={handleBookAppointment}>
            <Calendar className="w-4 h-4 mr-2" />
            {t("detail.actions.book")}
          </Button>,
        ]
      : []),
    // Always show Edit button
    <Can key="edit" I="update" a="Customer">
      <Button variant="ghost" size="sm" onClick={handleEdit}>
        <Edit className="w-4 h-4" />
        {!isMobile && <span className="ml-2">{t("detail.actions.edit")}</span>}
      </Button>
    </Can>,
  ].filter(Boolean);

  return (
    <>
      <BaseNavigationBar
        title={getCustomerDisplayName(customer)}
        subtitle={t("customerDetails")}
        backButton={<BackButton onBack={handleBack} />}
        trailingElements={trailingElements}
      />
      <PageContent>
        <CustomerDetailView
          customer={customer}
          onBack={handleBack}
          onEdit={handleEdit}
          onCall={handleCall}
          onEmail={handleEmail}
          onBookAppointment={handleBookAppointment}
          showMobileActions={isMobile}
        />
      </PageContent>
    </>
  );
}
